// Consolidated User types with Pod and Tag assignments

import { PodClientAssignment } from './pods';
import { TagAssignment } from './tags';

export interface UserProfile {
  // Basic Information
  user_profile_id: string;
  client_id?: string | null;
  user?: string; // user id reference

  // Personal Details
  first_name: string;
  last_name: string;
  title?: string | null;
  gender: string;
  date_of_birth?: string | null;
  requires_lmp?: boolean;
  profile_picture?: string | null;

  // Contact Information
  email?: string;
  phone_number: string;
  alternate_phone?: string | null;
  alternate_email?: string | null;
  preferred_contact_method: string;

  // Address
  address_line_1?: string | null;
  address_line_2?: string | null;
  house_no?: string | null;
  street?: string | null;
  landmark?: string | null;
  city: string;
  state?: string | null;
  country?: string | null;
  zipcode?: string | null;

  // Professional Information
  role: string; // SUPER_ADMIN, ADMIN, DOCTOR, CLINICAL_SUPPORT, OPERATIONS_SUPPORT, CLIENT, READ_ONLY, etc.
  occupation: string;
  organization?: string | null;
  clinic?: string | null;
  appointment_duration: string;

  // Doctor-specific fields
  signature?: string | null;
  signature_file_name?: string | null;
  specialization?: string | null;
  registration_number?: string | null;
  registration_state?: string | null;

  // Emergency Contact
  emergency_contact_name?: string | null;
  emergency_contact_relation?: string | null;
  emergency_contact_number?: string | null;

  // Health Information (for clients)
  health_goals?: Record<string, any>;
  client_story?: Record<string, any>;
  lifestyle_history?: Record<string, any>;
  family_history?: Record<string, any>;

  // HIPAA Compliance
  hipaa_consent: boolean;
  hipaa_consent_date?: string | null;
  hipaa_consent_version?: string | null;

  // Pod Assignments (new fields)
  pod_assignments?: PodClientAssignment[];
  active_pod_assignments?: PodClientAssignment[];
  primary_pod?: {
    pod_id: string;
    pod_name: string;
    assignment_id: string;
    start_date: string;
    end_date?: string;
    metadata?: Record<string, any>;
    metadata_schema?: Record<string, any>;
  };
  // Pods list with metadata
  pods?: Array<{
    pod_id: string;
    name: string;
    type: string;
    role?: string;
    assignment_type?: string;
    association_type: 'member' | 'client';
    is_active: boolean;
    metadata?: Record<string, any>;
    metadata_schema?: Record<string, any>;
  }>;

  // Tag Assignments (new fields)
  tag_assignments?: TagAssignment[];
  tags?: string[]; // simplified list of tag_ids
  tag_names?: string[]; // simplified list of tag names for display

  // Timestamps
  created_at?: string;
  updated_at?: string;
}

// Extended User type (used in some contexts)
export interface User {
  id: string;
  uuid: string;
  Username: string;
  email: string;
  role: string;
  Attributes?: Array<{ Name: string; Value: string }>;
  UserCreateDate: string;
  UserLastModifiedDate: string;
  Enabled: boolean;
  UserStatus: string;
  profile: UserProfile;
}

// Simplified user for lists and dropdowns
export interface SimpleUser {
  id: string;
  user_profile_id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  profile_picture?: string;
  primary_pod?: {
    pod_id: string;
    pod_name: string;
  };
}

// User role constants — must mirror the backend Role enum at
// superclinic-api/superclinic/users/domains/profile_management/models.py
export const USER_ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  DOCTOR: 'DOCTOR',
  CLIENT: 'CLIENT',
  CORPORATE_ADMIN: 'CORPORATE_ADMIN',
  BUSINESS_DEVELOPMENT: 'BUSINESS_DEVELOPMENT',
  READ_ONLY: 'READ_ONLY',
  OPERATIONS_SUPPORT: 'OPERATIONS_SUPPORT',
  CLINICAL_SUPPORT: 'CLINICAL_SUPPORT',
  NUTRITIONIST: 'NUTRITIONIST',
  PHARMACIST: 'PHARMACIST',
  SUPER_DOCTOR: 'SUPER_DOCTOR',
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// Role display names
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  DOCTOR: 'Doctor',
  CLIENT: 'Client',
  CORPORATE_ADMIN: 'Corporate Admin',
  BUSINESS_DEVELOPMENT: 'Business Development',
  READ_ONLY: 'Read Only',
  OPERATIONS_SUPPORT: 'Reception',
  CLINICAL_SUPPORT: 'Clinical Support',
  NUTRITIONIST: 'Nutritionist',
  PHARMACIST: 'Pharmacist',
  SUPER_DOCTOR: 'Super Doctor',
};

// Helper to check if user is a healthcare provider (can be in pods)
// Helper to check if user is a healthcare provider (can be in pods).
// Accepts current roles and legacy specialty aliases that map to CLINICAL_SUPPORT.
export const isHealthcareProvider = (role: string): boolean => {
  const normalized = String(role ?? '')
    .trim()
    .toUpperCase()
    .replace(/^ROLE_/, '');
  if (
    normalized === USER_ROLES.DOCTOR ||
    normalized === USER_ROLES.SUPER_DOCTOR ||
    normalized === USER_ROLES.CLINICAL_SUPPORT ||
    normalized === USER_ROLES.NUTRITIONIST ||
    normalized === USER_ROLES.PHARMACIST
  ) {
    return true;
  }
  return ['NURSE', 'THERAPIST', 'HEALTH_COACH', 'RELATIONSHIP_MANAGER'].includes(normalized);
};

// Helper to check if user can manage pods
export const canManagePods = (role: string): boolean => {
  const allowed: string[] = [USER_ROLES.SUPER_ADMIN, USER_ROLES.SUPER_DOCTOR, USER_ROLES.DOCTOR];
  return allowed.includes(role);
};

// Helper to check if user can manage tags
export const canManageTags = (role: string): boolean => {
  const allowed: string[] = [
    USER_ROLES.SUPER_ADMIN,
    USER_ROLES.ADMIN,
    USER_ROLES.BUSINESS_DEVELOPMENT,
  ];
  return allowed.includes(role);
};

// Helper to check if user can view pods tab
export const canViewPodsTab = (role: string): boolean => {
  return role !== USER_ROLES.CLIENT;
};

// Helper to check if a user is read-only (auditor / observer).
// Use this to gate edit/create/delete UI — prefer wrapping controls
// Prefer permission-derived read-only state from usePermissions() in components.
export const isReadOnly = (role?: string | null): boolean => {
  return role === USER_ROLES.READ_ONLY;
};

// User creation request with pod assignments
export interface CreateUserRequest {
  // Basic user fields
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  role: string;

  // Optional fields
  title?: string;
  gender?: string;
  date_of_birth?: string;
  occupation?: string;
  organization?: string;
  clinic?: string;

  // Address
  address_line_1?: string;
  address_line_2?: string;
  house_no?: string;
  street?: string;
  landmark?: string;
  city?: string;
  state?: string;
  country?: string;
  zipcode?: string;

  // Pod assignments (new)
  pod_assignments?: Array<{
    pod_id: string;
    assignment_type: 'primary' | 'secondary';
    start_date: string;
    end_date?: string;
    role?: 'lead' | 'member' | 'backup'; // for healthcare providers
  }>;

  // Tag assignments (new)
  tag_ids?: string[];
}

// User update request
export interface UpdateUserRequest extends Partial<CreateUserRequest> {
  user_profile_id: string;
}

// User filters for search
export interface UserFilters {
  role?: string;
  search?: string;
  pod_id?: string;
  has_pod?: boolean;
  tag_ids?: string[];
  is_active?: boolean;
  page?: number;
  page_size?: number;
}

// Paginated users response
export interface PaginatedUsersResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: UserProfile[];
}
